import { useTranslation } from "react-i18next";
import React, { useEffect, useState } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";
import { apiClient, formatApiError } from "@/lib/api";
import { formatGNF } from "@/utils/format";
import { toast } from "sonner";

/** Écran de décision admin : annulation d'une commande DÉJÀ récupérée par le livreur.
 * Restaurant et livreur payés intégralement (non modifiable) ; remboursement client
 * au cas par cas ; devenir du colis à choisir. */
export default function CancelInDeliveryModal({ order, onClose, onDone }) {
  const { t } = useTranslation();
  const [share, setShare] = useState(0.7);
  const [reason, setReason] = useState("");
  const [packageAction, setPackageAction] = useState("return_to_restaurant");
  const [clientRefund, setClientRefund] = useState("none");
  const [refundAmount, setRefundAmount] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiClient.get("/config/driver-share").then(({ data }) => setShare(data.share)).catch(() => {});
  }, []);

  const total = order.total || 0;
  const partRestaurant = Math.max(0, (order.subtotal || 0) - (order.commission || 0));
  const partLivreur = Math.round((order.delivery_fee || 0) * share);
  const onlinePaid = (order.payment_method || "") !== "cash" && order.payment_status === "paid";

  const submit = async () => {
    if (reason.trim().length < 3) {
      toast.error(t("admin.reasonMandatory"));
      return;
    }
    if (clientRefund === "partial" && (!refundAmount || Number(refundAmount) <= 0 || Number(refundAmount) >= total)) {
      toast.error(`Montant partiel invalide (entre 1 et ${total - 1} GNF)`);
      return;
    }
    setSaving(true);
    try {
      await apiClient.post(`/admin/orders/${order.order_id}/cancel-in-delivery`, {
        reason: reason.trim(),
        package_action: packageAction,
        client_refund: clientRefund,
        refund_amount: clientRefund === "partial" ? Number(refundAmount) : null,
      });
      toast.success(t("admin.cidDone"));
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 grid place-items-center p-4" data-testid="cancel-in-delivery-modal">
      <div className="bg-white rounded-2xl w-full max-w-lg p-6 max-h-[92vh] overflow-y-auto">
        <h3 className="font-display text-lg font-semibold flex items-center gap-2 mb-1">
          <AlertTriangle className="w-5 h-5 text-brand-red" /> Annulation en cours de livraison
        </h3>
        <p className="text-sm text-mute mb-4">
          #{order.order_id.slice(-6).toUpperCase()} · {order.restaurant_name} — le livreur est déjà en route.
          {t("admin.cidHintA")} <b>{t("admin.cidHintB")}</b> {t("admin.cidHintC")}
        </p>

        {/* Récapitulatif non modifiable */}
        <div className="rounded-xl bg-elevated border border-sand p-4 text-sm space-y-2 mb-4" data-testid="cid-recap">
          <div className="flex justify-between"><span className="text-mute">Montant total commande</span><b>{formatGNF(total)}</b></div>
          <div className="flex justify-between">
            <span className="text-mute">{t("admin.cidRestoShare")}</span>
            <b className="text-brand-green" data-testid="cid-part-restaurant">{formatGNF(partRestaurant)} — payée</b>
          </div>
          <div className="flex justify-between">
            <span className="text-mute">Part livreur (trajet engagé, {Math.round(share * 100)}%)</span>
            <b className="text-brand-green" data-testid="cid-part-livreur">{formatGNF(partLivreur)} — payée</b>
          </div>
          <div className="flex justify-between text-xs text-mute pt-1 border-t border-sand">
            <span>Paiement</span><span className="uppercase">{order.payment_method} · {order.payment_status}</span>
          </div>
        </div>

        {/* Motif obligatoire */}
        <label className="block mb-4">
          <span className="text-xs uppercase tracking-widest text-mute block mb-1.5">Motif (obligatoire)</span>
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
            placeholder={t("admin.cidPh")}
            data-testid="cid-reason"
            className="w-full bg-white border border-sand rounded-xl px-3 py-2.5 text-sm outline-none focus:border-brand-red" />
        </label>

        {/* Devenir du colis */}
        <div className="mb-4">
          <span className="text-xs uppercase tracking-widest text-mute block mb-1.5">Devenir du colis (consigne au livreur)</span>
          <div className="grid grid-cols-2 gap-2">
            {[["return_to_restaurant", "Ramener au restaurant"], ["keep_driver", "Le livreur garde le colis"]].map(([v, label]) => (
              <button key={v} type="button" onClick={() => setPackageAction(v)} data-testid={`cid-package-${v}`}
                className={`rounded-xl px-3 py-2.5 text-sm font-semibold border transition-colors ${
                  packageAction === v ? "bg-ink text-white border-ink ring-2 ring-ink ring-offset-1" : "bg-white border-sand hover:border-brand-red"}`}>
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Remboursement client — décision admin */}
        <div className="mb-5">
          <span className="text-xs uppercase tracking-widest text-mute block mb-1.5">Remboursement du client</span>
          {!onlinePaid ? (
            <div className="rounded-xl bg-[#FFF9E6] border border-[#F4E3AD] px-3 py-2.5 text-sm text-[#8A6D00]" data-testid="cid-refund-cash-note">
              Commande {order.payment_method === "cash" ? "cash non encaissée" : "non payée"} — aucun remboursement à traiter.
            </div>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-2">
                {[["none", "Aucun"], ["partial", "Partiel"], ["full", `Total (${formatGNF(total)})`]].map(([v, label]) => (
                  <button key={v} type="button" onClick={() => setClientRefund(v)} data-testid={`cid-refund-${v}`}
                    className={`rounded-xl px-2 py-2.5 text-xs font-semibold border transition-colors ${
                      clientRefund === v ? "bg-ink text-white border-ink ring-2 ring-ink ring-offset-1" : "bg-white border-sand hover:border-brand-red"}`}>
                    {label}
                  </button>
                ))}
              </div>
              {clientRefund === "partial" && (
                <input type="number" min={1} max={total - 1} value={refundAmount}
                  onChange={(e) => setRefundAmount(e.target.value)}
                  placeholder={`Montant en GNF (max ${total - 1})`}
                  data-testid="cid-refund-amount"
                  className="mt-2 w-full bg-white border border-sand rounded-xl px-3 py-2.5 text-sm outline-none focus:border-brand-red" />
              )}
              <p className="text-[11px] text-mute mt-1.5">
                Aucun remboursement automatique à ce stade — c'est votre décision qui déclenche (ou non) le refund.
              </p>
            </>
          )}
        </div>

        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 rounded-full border border-sand px-4 py-2.5 text-sm font-semibold hover:border-ink">
            Retour
          </button>
          <button onClick={submit} disabled={saving} data-testid="cid-confirm"
            className="flex-1 rounded-full bg-brand-red hover:bg-[#C11E2C] disabled:opacity-50 text-white px-4 py-2.5 text-sm font-semibold inline-flex items-center justify-center gap-2">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Valider l'annulation
          </button>
        </div>
      </div>
    </div>
  );
}
